import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Card, Flex, Form, Select, Typography } from 'antd';

import isEmpty from 'lodash.isempty';

import axios from '../../../axios';

import { isShapeInsideBoundary } from '../../../helpers/map';
import { hasPermission } from '../../../helpers/user';

import { MAPS_TAB } from '../../../constants/map';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';

import { useModal } from '../../../hooks/useModal';
import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { fetchFarms } from '../../../redux/actions/farm';
import { fetchGrazingData, fetchGrazingHeatmapStyles } from '../../../redux/actions/grazing';
import { createSite, fetchSite, updateSite } from '../../../redux/actions/site';
import { resetSite } from '../../../redux/reducers/site';

import AppModal from '../../../components/AppModal';
import CancelSaveButtons from '../../../components/CancelSaveButtons';
import LoadingBar from '../../../components/LoadingBar';
import MapSite from '../../../components/Maps/MapSite';
import { DEFAULT_SHAPE_COLOR } from '../../../components/Maps/constants';
import PrevNextButtons from '../../../components/PrevNextButtons';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../../components/action_notifier';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';
import AppDropdown from '../../ui-components/AppDropdown';

import SiteForm from './SiteForm';
import SiteHistory from './SiteHistory';
import './styles.css';

const { Title } = Typography;

function Site() {
  const dispatch = useDispatch();
  const history = useHistory();

  const [farmForm] = Form.useForm();

  const formRef = useRef(null);

  const { id } = useParams();

  const isNewMode = id === 'new';

  const userState = useSelector(state => state.user);
  const { list: farms, loading } = useSelector(state => state.farm);
  const { site, loading: siteLoading } = useSelector(state => state.site);
  const { data: grazing } = useSelector(state => state.grazing);

  const [archiveModalVisible, openArchiveModal, closeArchiveModal] = useModal();

  const [isEditMode, setEditMode] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    color: isNewMode ? DEFAULT_SHAPE_COLOR : site?.color,
    geofences: [],
    selectedFarmGeofences: [],
    selectedGeofence: null,
    selected_farm: {},
    selected_geofences: [],
    site_types: [],
    site: {
      geo_json: {},
      geofences: [],
      identifier: '',
      name: '',
      rules: []
    }
  });

  useEffect(() => {
    fetchData();

    return () => dispatch(resetSite());
  }, []);

  useEffect(() => {
    mapFarmsData();
  }, [farms]);

  useEffect(() => {
    if (!site) return;

    setState(prevState => ({
      ...prevState,
      color: site?.color,
      selected_farm: farms?.filter(x => x.value === site?.farm_id)[0],
      selected_geofences: site?.geofences?.map(x => ({ label: x.name, value: x.id }))
    }));
  }, [site]);

  useEffect(() => {
    setEditMode(isNewMode);
  }, [id]);

  useEffect(() => {
    if (!state.selected_farm) return;

    setState(prevState => ({ ...prevState, selectedGeofence: null }));
    farmForm?.setFieldsValue({
      selected_farm: state.selected_farm,
      selected_geofence: state.selectedFarmGeofences.find(geofence => geofence.is_master)
    });
  }, [state.selected_farm]);

  async function fetchData() {
    try {
      await Promise.all([
        getSiteTypes(),
        getFarms(),
        getSites(id),
        getGrazingData(),
        dispatch(fetchGrazingHeatmapStyles())
      ]);
    } catch (error) {
      errorToastHandler(messages.FAILED_ON_FETCH_DATA);
    }
  }

  const navigateToThePrevPage = useCallback(() => {
    history.push(`/sites/${site?.prev_id}`);
  }, [site?.prev_id]);

  const navigateToTheNextPage = useCallback(() => {
    history.push(`/sites/${site?.next_id}`);
  }, [site?.next_id]);

  const setColor = useCallback(color => {
    setState(prevState => ({ ...prevState, color }));
  }, []);

  const setSiteGeoJson = useCallback(geo_json => {
    setIsFormDirty(true);
    setSubmittable(true);
    setState(prevState => ({ ...prevState, site: { ...prevState.site, geo_json: { ...geo_json } } }));
  }, []);

  async function getFarms() {
    return dispatch(fetchFarms({ with_details: true }));
  }

  async function getGrazingData() {
    if (!site) return;

    const siteGeofence = site?.geofences?.at(0);
    const rotationId = siteGeofence?.currentRotation
      ? siteGeofence?.currentRotation?.id
      : siteGeofence?.lastRotation?.id;

    if (!rotationId) return;

    return dispatch(fetchGrazingData({ rotationIds: [rotationId] }));
  }

  async function getSites(id = null) {
    if (isNewMode) return;

    return dispatch(fetchSite({ id }));
  }

  async function getSiteTypes() {
    const response = await axios.get('/sites/types');

    if (response.status === 200) setState(prevState => ({ ...prevState, site_types: response.data }));
  }

  const updatedFarms = useMemo(
    () =>
      farms?.map(f => ({
        value: f.id,
        label: f.name,
        meta: {
          address: f.address.full_address,
          master_geofences: f.master_geofence,
          features: f.geofences,
          sites: f.sites
        }
      })),
    [farms]
  );

  function mapFarmsData() {
    if (!farms?.length) return;

    const farm = updatedFarms?.at(0);

    setState(prevState => ({
      ...prevState,
      farms: updatedFarms,
      geofences: farm?.meta?.features?.map(feature => ({
        label: feature.name,
        value: feature.id
      })),
      selected_farm: farm,
      selected_geofences: site?.geofences?.map(x => ({ label: x.name, value: x.id })),
      selectedFarmGeofences: farm?.meta?.features?.map(feature => ({
        label: feature?.geo_json?.properties?.name,
        value: feature?.geo_json?.properties?.id,
        is_master: feature?.geo_json?.properties?.is_master
      })),
      site: {
        ...prevState.site,
        farm_id: farm?.value,
        address: farm?.meta.address,
        features: farm?.meta.features
      }
    }));
  }

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.SITES.UPDATE_OWN], userState?.permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            onClick={() => {
              setEditMode(true);
              setSubmittable(false);
            }}>
            Edit site
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.SITES.DELETE_OWN], userState?.permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" onClick={openArchiveModal}>
            Archive site
          </Button>
        )
      });
    }

    return availableActions;
  }, [userState?.permissions]);

  async function createNewSite(data) {
    const siteFormValues = await formRef.current?.validateFields();
    const geofence = await farmForm.getFieldValue('selected_geofence');
    const payload = {
      ...state.site,
      ...siteFormValues,
      color: state.color,
      geofence_ids: [typeof geofence === 'number' ? geofence : geofence?.value]
    };

    return dispatch(createSite(payload)).unwrap();
  }

  async function updateCurrentSite() {
    const siteFormValues = await formRef.current?.validateFields();
    const payload = {
      ...site,
      ...siteFormValues,
      color: state.color,
      geo_json: isEmpty(state.site.geo_json) ? site?.geo_json : state.site.geo_json,
      geofence_ids: site?.geofences?.map(geofence => geofence.id)
    };

    if (isEmpty(payload.geo_json)) return;

    const boundary = site?.geofences?.at(0)?.geo_json;

    if (boundary && !isShapeInsideBoundary(boundary, payload.geo_json)) {
      warnToastHandler(messages.SHAPE_OUTSIDE_BOUNDARIES('site'));

      return;
    }

    try {
      await dispatch(updateSite({ id, data: payload })).unwrap();

      setIsFormDirty(false);
      setEditMode(false);
      successToastHandler(messages.UPDATED);
    } catch (error) {
      errorToastHandler(messages.FAILED_ON_UPDATE_DATA);
    }
  }

  const handleSaveSite = async () => {
    if (isNewMode) {
      const boundary = state.selectedGeofence?.geo_json || state.selected_farm?.meta?.features?.at(0)?.geo_json;

      if (isEmpty(state.site?.geo_json)) {
        warnToastHandler(messages.DATA_REQUIRED);

        return;
      }

      if (!isShapeInsideBoundary(boundary, state.site?.geo_json)) {
        warnToastHandler(messages.SHAPE_OUTSIDE_BOUNDARIES('site'));

        return;
      }

      try {
        await createNewSite();

        setIsFormDirty(false);
        successToastHandler(messages.CREATED);
        history.push(`/maps?tab=${MAPS_TAB.SITE}`);
      } catch (error) {
        errorToastHandler(messages.FAILED_ON_CREATE_DATA('site'));
      }
    } else {
      try {
        await updateCurrentSite();
        await fetchData();
      } catch (error) {
        errorToastHandler(error?.message);
      }
    }
  };

  async function handleSaveSiteAndAddNew() {
    if (isNewMode) {
      const boundary = state.selectedGeofence?.geo_json || state.selected_farm?.meta?.features?.at(0)?.geo_json;

      if (!isShapeInsideBoundary(boundary, state.site?.geo_json)) {
        warnToastHandler(messages.SHAPE_OUTSIDE_BOUNDARIES('site'));

        return;
      }

      try {
        await createNewSite();
        await resetPage();

        setIsFormDirty(false);
      } catch (error) {
        errorToastHandler(messages.FAILED_ON_CREATE_DATA('site'));
      }

      successToastHandler(messages.CREATED);
    } else {
      try {
        await updateCurrentSite({ ...site, color: state.color });
        history.push('/sites/new');
      } catch (error) {
        errorToastHandler(error?.message);
      }
    }
  }

  async function resetPage() {
    formRef?.current?.resetFields();

    setState(prevState => ({
      ...prevState,
      color: DEFAULT_SHAPE_COLOR,
      selected_farm: {},
      selected_geofences: [],
      site_types: [],
      site: {
        geo_json: null,
        geofences: [],
        identifier: '',
        name: '',
        rules: []
      }
    }));

    await Promise.all([getSiteTypes(), getFarms()]);
  }

  async function deleteSite() {
    try {
      const response = await axios.delete(`sites/${id}`);

      if (response.status === 200) {
        successToastHandler(messages.ARCHIVED);
        history.push(`/maps?tab=${MAPS_TAB.SITE}`);
      }
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_DELETE_DATA);
    }
  }

  const setSelectedFarm = selectedFarmId => {
    const selectedFarm = state.farms?.find(f => f.value === selectedFarmId);

    setIsFormDirty(true);
    setState(prevState => ({
      ...prevState,
      selected_farm: selectedFarm,
      selectedFarmGeofences: selectedFarm?.meta?.features?.map(feature => ({
        label: feature?.geo_json?.properties?.name,
        value: feature?.geo_json?.properties?.id,
        is_master: feature?.geo_json?.properties?.is_master
      })),
      site: {
        ...prevState.site,
        farm_id: selectedFarm?.value,
        address: selectedFarm?.meta.address,
        features: selectedFarm?.meta.features
      },
      editing: true
    }));
  };

  const setSelectedGeofence = selectedGeofenceId => {
    const selectedGeofence = state.selected_farm?.meta?.features?.find(f => f.id === selectedGeofenceId);

    setIsFormDirty(true);
    setState(prevState => ({
      ...prevState,
      site: {
        ...prevState.site,
        geofence_ids: [selectedGeofence?.id]
      },
      selectedGeofence
    }));
  };

  const breadcrumbItems = [
    {
      title: <Link to={`/maps?tab=${MAPS_TAB.SITE}`}>List of Sites</Link>
    },
    {
      title: `${
        isEditMode && !isNewMode ? `Edit Site ${site?.name}` : isNewMode ? 'Create New Site' : `Site ${site?.name}`
      }`
    }
  ];

  const cancelEditing = useCallback(async () => {
    setEditMode(false);

    isNewMode ? history.push(`/maps?tab=${MAPS_TAB.SITE}`) : await getSites(id);
  }, [getSites, id, isEditMode, isNewMode]);

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (submittable) {
          await handleSaveSite();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: async () => {
        setIsFormDirty(false);
        await cancelEditing();
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(async () => {
    isFormDirty ? triggerUnsavedChangesPrompt() : await cancelEditing();
  }, [isFormDirty]);

  const updateSubmittable = useCallback(
    validationState => {
      setSubmittable(validationState);
    },
    [setSubmittable]
  );

  const deleteGeoJson = useCallback(() => {
    setIsFormDirty(true);
    setState(prevState => ({ ...prevState, site: { ...prevState.site, geo_json: {} } }));
    setSubmittable(false);
  }, []);

  const isSaveAllowed = submittable && !isEmpty(isEditMode && !isNewMode ? site?.geo_json : state.site?.geo_json);

  return (
    <>
      {loading && !farms?.length && !state.selected_farm?.meta?.features?.length ? (
        <LoadingBar fullscreen={true} />
      ) : (
        <>
          {NavigationPrompt}
          <Flex gap="small" vertical={false} justify="space-between" wrap>
            <Flex vertical={true}>
              <Title level={4}>
                {isEditMode && !isNewMode
                  ? `Edit Site ${site?.name}`
                  : isNewMode
                    ? 'Create New Site'
                    : `Site ${site?.name}`}
              </Title>

              <AppBreadcrumb items={breadcrumbItems} />
            </Flex>
            <Flex gap="small">
              {!isEditMode && !isNewMode && !!actions?.length && <AppDropdown label="Actions" items={actions} />}

              {isEditMode && (
                <CancelSaveButtons
                  disabled={!isSaveAllowed}
                  handleCancel={handleCancel}
                  handleSave={handleSaveSite}
                  handleSaveAndNavigate={isNewMode ? handleSaveSiteAndAddNew : undefined}
                />
              )}

              {!isNewMode && (
                <PrevNextButtons
                  nextId={site?.next_id}
                  prevId={site?.prev_id}
                  handlePrev={navigateToThePrevPage}
                  handleNext={navigateToTheNextPage}
                />
              )}
            </Flex>
          </Flex>

          {isNewMode && (
            <Card>
              <Form
                className="farm-form"
                labelWrap
                form={farmForm}
                layout="horizontal"
                labelCol={{ span: 2 }}
                initialValues={{
                  selected_farm: state.farms?.at(0),
                  selected_geofence: state.selectedFarmGeofences?.at(0)
                }}>
                <Flex vertical={true} gap="small">
                  <Form.Item className="w-full" labelAlign="left" label="Farm" name="selected_farm" colon={false}>
                    <Select className="w-full" options={state.farms} onChange={setSelectedFarm} />
                  </Form.Item>
                  <Form.Item
                    className="w-full"
                    labelAlign="left"
                    label="Pasture"
                    name="selected_geofence"
                    colon={false}>
                    <Select
                      className="w-full"
                      disabled={!state.selected_farm}
                      options={state.selectedFarmGeofences}
                      onChange={setSelectedGeofence}
                    />
                  </Form.Item>
                </Flex>
              </Form>
            </Card>
          )}

          <MapSite
            color={state.color}
            editing={isEditMode}
            farm={state.selected_farm}
            grazing={grazing}
            loading={siteLoading}
            new={isNewMode}
            selected={state.selectedGeofence}
            site={isNewMode ? state.site : site}
            handleCancelEditing={handleCancel}
            handleDelete={deleteGeoJson}
            handleSaveEditing={handleSaveSite}
            onCreate={setSiteGeoJson}
            onEdit={setSiteGeoJson}
          />

          <SiteForm
            color={state.color}
            editing={isEditMode}
            formRef={formRef}
            geofences={state.geofences}
            isNew={isNewMode}
            site={isNewMode ? state.site : site}
            types={state.site_types}
            handleColorChange={setColor}
            onChange={setIsFormDirty}
            handleValidationChange={updateSubmittable}
          />

          {isEditMode && (
            <Flex justify="end" className="mb-2">
              <CancelSaveButtons
                disabled={!isSaveAllowed}
                handleCancel={handleCancel}
                handleSave={handleSaveSite}
                handleSaveAndNavigate={isNewMode ? handleSaveSiteAndAddNew : undefined}
              />
            </Flex>
          )}

          {!isNewMode && <SiteHistory siteId={id} />}
        </>
      )}

      <AppModal
        isOpen={archiveModalVisible}
        confirmButtonColor="danger"
        confirmButtonText="Archive"
        title="Archive site"
        handleCancel={closeArchiveModal}
        handleConfirm={deleteSite}>
        <div className="py-4">{messages.ARCHIVE_CONFIRMATION('site')}</div>
      </AppModal>

      <UnsavedChangesModal
        isOpen={modalProps.visible}
        handleCancel={modalProps.onCancel}
        handleConfirm={modalProps.onSave}
        handleDiscard={modalProps.onDiscard}
      />
    </>
  );
}

export default memo(Site);
